import React from 'react';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { AlertTriangle, MessageSquare } from 'lucide-react';

const BSAwardTab = ({ topBsCaller, bsLeaderboard, bsFeed, bsSort, setBsSort, callBs, renderMedia, period }) => (
  <div className="space-y-6">
    {topBsCaller && (
      <Card className="p-5 bg-gradient-to-r from-amber-500 to-yellow-600 text-white rounded-2xl" data-testid="top-bs-caller-banner">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-white/20 rounded-xl"><AlertTriangle className="w-8 h-8" /></div>
          <div>
            <p className="text-sm opacity-90">Top BS Caller</p>
            <p className="text-xl font-bold">{topBsCaller.user?.name || 'Unknown'}</p>
            <p className="text-sm opacity-80">@{topBsCaller.user?.username} · {topBsCaller.total_bs} BS calls · {topBsCaller.post_count} posts</p>
          </div>
        </div>
      </Card>
    )}

    <Card className="p-4 rounded-2xl border-2 border-dashed border-amber-200 dark:border-amber-800" data-testid="bs-how-to">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-amber-100 dark:bg-amber-900/40 rounded-xl"><AlertTriangle className="w-5 h-5 text-amber-600" /></div>
        <div>
          <h3 className="font-bold text-sm text-gray-900 dark:text-white">Call out the BS</h3>
          <p className="text-xs text-gray-500">Include <span className="font-bold text-amber-600">#BSAward</span> in your post to nominate it. The community decides what's BS!</p>
        </div>
      </div>
    </Card>

    <div className="flex items-center gap-2">
      <span className="text-sm text-gray-500 font-medium">Sort by:</span>
      <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-full p-1">
        <button onClick={() => setBsSort('recent')} className={`px-4 py-1.5 rounded-full text-xs font-bold transition-colors ${bsSort === 'recent' ? 'bg-white dark:bg-gray-700 text-amber-600 shadow-sm' : 'text-gray-500'}`} data-testid="bs-sort-recent">Newest</button>
        <button onClick={() => setBsSort('top')} className={`px-4 py-1.5 rounded-full text-xs font-bold transition-colors ${bsSort === 'top' ? 'bg-white dark:bg-gray-700 text-amber-600 shadow-sm' : 'text-gray-500'}`} data-testid="bs-sort-top">Most BS</button>
      </div>
    </div>

    <Card className="rounded-2xl overflow-hidden" data-testid="bs-leaderboard">
      <div className="p-5 bg-gradient-to-r from-amber-500 to-yellow-600 text-white">
        <h3 className="text-lg font-bold flex items-center gap-2"><AlertTriangle className="w-5 h-5" /> Top 10 BS Awards · {period === 'month' ? 'This Month' : 'All Time'}</h3>
      </div>
      <div className="divide-y dark:divide-gray-700">
        {bsLeaderboard.length === 0 ? (
          <div className="p-8 text-center text-gray-500" data-testid="bs-leaderboard-empty"><AlertTriangle className="w-10 h-10 mx-auto mb-2 text-gray-300" /><p>No BS called yet. Post with #BSAward to get started!</p></div>
        ) : bsLeaderboard.map((post) => (
          <div key={post.post_id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition" data-testid={`bs-rank-${post.rank}`}>
            <div className="flex items-start gap-3">
              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${post.rank === 1 ? 'bg-amber-400 text-amber-900' : post.rank === 2 ? 'bg-amber-300 text-amber-800' : post.rank === 3 ? 'bg-amber-200 text-amber-700' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'}`}>{post.rank}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold text-sm">{post.author?.name || 'Unknown'}</span>
                  <span className="text-xs text-gray-500">@{post.author?.username}</span>
                </div>
                <p className="text-gray-800 dark:text-gray-200 text-sm mb-1">{post.content}</p>
                {renderMedia(post)}
              </div>
              <button onClick={() => callBs(post.post_id)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-bold transition-all ${post.user_bs ? 'bg-amber-500 text-white shadow-lg' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-amber-100'}`}
                data-testid={`bs-btn-${post.post_id}`}>
                <AlertTriangle className="w-4 h-4" /><span>{post.bs_count || 0}</span>
              </button>
            </div>
          </div>
        ))}
      </div>
    </Card>

    {bsFeed.length > 0 && (
      <Card className="rounded-2xl overflow-hidden" data-testid="bs-feed">
        <div className="p-4 border-b dark:border-gray-700"><h3 className="font-bold flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Latest BS Calls</h3></div>
        <div className="divide-y dark:divide-gray-700">
          {bsFeed.map((post) => (
            <div key={post.post_id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition" data-testid={`bs-feed-${post.post_id}`}>
              <div className="flex items-start gap-3">
                <Avatar className="w-9 h-9 flex-shrink-0"><AvatarImage src={post.author?.avatar_url} /><AvatarFallback>{post.author?.name?.charAt(0) || '?'}</AvatarFallback></Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-sm">{post.author?.name}</span>
                    <span className="text-xs text-gray-500">@{post.author?.username}</span>
                  </div>
                  <p className="text-gray-800 dark:text-gray-200 text-sm">{post.content}</p>
                  {renderMedia(post)}
                </div>
                <button onClick={() => callBs(post.post_id)}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold ${post.user_bs ? 'bg-amber-500 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600'}`}
                  data-testid={`bs-feed-btn-${post.post_id}`}>
                  <AlertTriangle className="w-3.5 h-3.5" /> {post.bs_count || 0}
                </button>
              </div>
            </div>
          ))}
        </div>
      </Card>
    )}
  </div>
);

export default BSAwardTab;
